import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { useI18n } from '../../lib/i18n.js';
import { Card, Badge } from '../../components/ui.js';
import { QueryState } from '../../components/form.js';
import { ParentMedGrid } from './MedGrid.js';
import { SheetImport } from '../../components/SheetImport.js';

interface ScheduleSpec { frequencyType: string; times: string[] }
interface Order {
  id: string;
  concept: string;
  brandName: string | null;
  form: string;
  doseAmount: string | null;
  doseUnit: string | null;
  schedule: ScheduleSpec;
  approvalStatus: string;
  parentInstructions: string | null;
  isAdjunct: boolean;
}

export function ParentPlan() {
  const { t, locale } = useI18n();
  const q = useQuery({ queryKey: ['orders'], queryFn: () => api.get<{ orders: Order[] }>('/medications/orders') });
  // Parents see the active (approved) regimen only — drafts are clinician-only.
  const orders = (q.data?.orders ?? []).filter((o) => o.approvalStatus === 'approved');

  return (
    <div>
      <h1 className="text-xl font-bold text-slate-800 mb-1">{t('weekly.title')}</h1>
      <p className="text-sm text-slate-500 mb-4">
        {t('disclaimer.short')}
      </p>
      <QueryState
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        isEmpty={orders.length === 0}
        emptyMessage="—"
        errorFallback={t('common.error')}
      >
        <div className="space-y-3">
          {orders.map((o) => (
            <Card key={o.id}>
              <div className="flex items-center justify-between">
                <div className="font-semibold text-slate-800">{t(`concept.${o.concept}`)}</div>
                {o.isAdjunct && <Badge tone="info">{t('meds.adjunct')}</Badge>}
              </div>
              {o.brandName && <div className="text-sm text-slate-500">{o.brandName}</div>}
              <div className="text-sm mt-1">
                {o.doseAmount ? `${o.doseAmount} ${o.doseUnit ?? ''} · ` : ''}
                {t(`freq.${o.schedule.frequencyType}`)}
                {o.schedule.times?.length ? ` · ${o.schedule.times.join(', ')}` : ''}
              </div>
              {o.parentInstructions && <div className="text-sm text-slate-600 mt-2">{o.parentInstructions}</div>}
            </Card>
          ))}
        </div>
      </QueryState>

      {/* Weekly medication tracking sheet (given doses over time). */}
      <div className="mt-6">
        <div className="mb-3">
          <Link to="/history" className="inline-block rounded-lg border border-brand-light bg-white px-3 py-2 text-sm font-medium text-brand-dark">
            {t('history.open')} →
          </Link>
        </div>
        <ParentMedGrid />
      </div>

      {/* Photo/PDF import of a FILLED paper follow sheet → reviewed entries. */}
      <div className="mt-6">
        <Card title={locale === 'en' ? 'Import a filled sheet (photo or PDF)' : 'Importer une fiche remplie (photo ou PDF)'}>
          <SheetImport />
        </Card>
      </div>
    </div>
  );
}
